'use client';

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

export default function MarkdownContent({ content, className = '' }: MarkdownContentProps) {
  const components: Components = {
        // Style headings
        h1: ({ children }) => (
          <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-3">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2">{children}</h3>
        ),
        // Style paragraphs
        p: ({ children }) => (
          <p className="text-gray-700 leading-relaxed mb-4">{children}</p>
        ),
        // Style lists
        ul: ({ children }) => (
          <ul className="list-none space-y-2 mb-4">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-inside space-y-2 mb-4 ml-4">{children}</ol>
        ),
        li: ({ children }) => (
          <li className="text-gray-700 leading-relaxed">{children}</li>
        ),
        // Style links
        a: ({ href, children }) => (
          <a
            href={href}
            className="text-primary-600 hover:text-primary-700 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        ),
        // Style strong/bold
        strong: ({ children }) => (
          <strong className="font-bold text-gray-900">{children}</strong>
        ),
        // Style emphasis/italic
        em: ({ children }) => (
          <em className="italic text-gray-700">{children}</em>
        ),
        // Style blockquotes
        blockquote: ({ children }) => (
          <blockquote className="border-l-4 border-primary-300 pl-4 italic text-gray-600 my-4">
            {children}
          </blockquote>
        ),
        // Style code
        code: ({ children }) => (
          <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono">
            {children}
          </code>
        ),
        // Style horizontal rules
        hr: () => <hr className="border-gray-300 my-8" />,
  };

  return (
    <div className={`prose prose-lg max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

